import type { ServicePageConfig } from "../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../package-pricing";

export const budtjänstPages: ServicePageConfig[] = [
  {
    // Bud — config för /bud
    // Generisk rikssida. Stadspecifika sidor: /bud-stockholm, /bud-goteborg
    // Fordon: el-driven moped, liten bil, skåpbil, stor skåpbil med bakgavellift.
    slug: "bud",
    serviceCategory: "budtjänst",

    meta: {
      title:
        "Boka Bud – Snabb Budleverans Samma Dag | Ungdomskraft",
      description:
        "Boka bud med leverans samma dag. Från kuvert till EU-pall – elmoped, bil eller skåpbil med bakgavellift. Expressbud inom 1 timme. Fast pris!",
      h1: "Boka Bud",
    },

    hero: {
      heading: "Snabb leverans, boka bud",
      intro:
        "Vi kör allt från kuvert till pallar med anpassat fordon för maximal effektivitet. Budet. Vi använder el-driven moped för det lilla budet, skåpbil med bakgavellift för det stora & tunga budet. Med Expressbud levereras ditt paket inom 1h.",
      heroImage: "/hero-bud.png",
      heroImageAlt:
        "Ungdomskraft budtjänst – ungdomar lastar paket i skåpbil för snabb budleverans",
    },

    featureCards: {
      headingText: "Vi sköter ditt bud",
      sinceText: "Budfirma sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft kört bud åt privatpersoner, företag och eventarrangörer. Vårt team av snabba ungdomar levererar allt från dokument till pallar – med den energi och flexibilitet som bara unga kan erbjuda.",
      bookYourText: "Boka ditt bud",
      bookDescText:
        "Beskriv vad du vill skicka, ange hämtadress och leveransadress. Du ser priset direkt och kan välja expressleverans.",
      vehicleTitleText: "Anpassat fordon för varje leverans",
      vehicleDescText:
        "Vi matchar fordon efter ditt gods. El-driven moped för brev och småpaket i city, liten bil för medelstora leveranser, skåpbil för större gods och stor skåpbil med bakgavellift för pallar och tungt gods. Du betalar bara för det fordon du behöver.",
    },

    itemsSection: {
      heading: "Vi kör allt – inga begränsningar",
      items: [
        {
          name: "Paket & kartonger",
          description:
            "E-handelspaket, returpaket, provkollektioner och presentförpackningar i alla storlekar",
          image: "/box.webp",
        },
        {
          name: "Elektronik & teknik",
          description:
            "Laptops, servrar, skärmar och annan elektronik som måste fram snabbt och säkert",
          image: "/electronics.webp",
        },
        {
          name: "Konst & skyltar",
          description:
            "Neonskyltar, tavlor, ljuslådor, banderoller och annat ömtåligt som kräver varsam hantering",
          image: "/painting.webp",
        },
        {
          name: "Möbler & inredning",
          description:
            "Kontorsmöbler, hyllor, lampor och inredningsdetaljer som ska levereras till kontor eller hem",
          image: "/furniture.webp",
        },
        {
          name: "Vitvaror & maskiner",
          description:
            "Reservdelar, maskindelar, vitvaror och tyngre gods – vi lyfter med bakgavellift vid behov",
          image: "/appliances.webp",
        },
        {
          name: "Cyklar & sportprylar",
          description:
            "Cyklar, elscootrar, träningsutrustning och sportprylar som ska till butik, event eller kund",
          image: "/road-bike.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Boka bud – snabb leverans utan begränsningar",
      mainText:
        "En budtjänst ska vara snabb, flexibel och pålitlig. Med Ungdomskraft får du precis det. Vi kör allt från ett enskilt kuvert till en hel EU-pall med gods – samma dag, ofta inom bara ett par timmar. Vårt team av unga, energiska budförare anpassar fordon efter vad du behöver skicka. Behöver du skicka ett kontrakt tvärs över stan innan klockan fyra? Vi skickar en elmoped som slinker genom trafiken på minuter. Ska en neonskylt levereras varsamt till en restaurang? Vi tar bilen och ser till att den kommer fram hel. Har du tre EU-pallar med energidryck som ska till ett event? Stor skåpbil med bakgavellift löser det smidigt. Det som skiljer oss från traditionella budföretag är kombinationen av hastighet och muskelkraft. Våra ungdomar bär inte bara – de springer. Trappor, trånga passager och tajta tidsfönster är vardag för oss. Vi vet att ditt bud ofta är brådskande, och vi behandlar varje leverans som om den vore den viktigaste på dagen. Vi tar emot uppdrag från privatpersoner, småföretag, restauranger, eventbyråer, advokatfirmor, arkitektkontor och alla andra som behöver få något levererat snabbt och säkert. Ingen leverans är för liten och ingen är för stor – vi löser det.",
      preparationHeading: "Så förbereder du ditt buduppdrag",
      preparationText:
        "Se till att godset är färdigpackat och redo att hämtas vid avtalad tid. Dokument bör ligga i kuvert eller mapp. Ömtåliga föremål som neonskyltar eller konst bör vara skyddade med bubbelplast eller kartong. Pallar bör vara plastade och tydligt märkta. Meddela oss om det finns portkod, trappor eller specialinstruktioner vid hämtning eller leverans. Om du behöver expressleverans – boka så tidigt som möjligt på dagen så kan vi ofta leverera inom en till två timmar. Ju mer information du ger oss i förväg, desto snabbare kan vi hämta och leverera. Ange alltid kontaktperson och telefonnummer vid både hämtning och leverans så att våra budförare kan nå rätt person direkt.",
      additionalSections: [
        {
          heading: "Anpassat fordon – från elmoped till bakgavellift",
          content:
            "Vi har byggt en flotta av fordon som täcker hela spektrumet av budleveranser. För de allra minsta leveranserna – brev, nycklar, dokument och småpaket – använder vi el-driven moped. Den tar sig snabbt genom innerstaden, parkerar var som helst och levererar på rekordtid. För medelstora paket, provkollektioner och ömtåliga föremål som neonskyltar skickar vi en liten bil. Den rymmer mer än mopeden men är fortfarande smidig i stadstrafik. När volymen ökar – flera kartonger, möbler eller större gods – tar vi skåpbilen. Den rymmer upp till tio kubikmeter och våra ungdomar lastar och bär allt. För de tyngsta uppdragen har vi stor skåpbil med bakgavellift. Den hanterar EU-pallar, tungt maskineri och stora volymer. Bakgavellyften gör att vi kan lasta och lossa pallar direkt från lastbrygga till markplan utan truck. Du väljer inte fordon själv – vi matchar automatiskt rätt fordon efter vad du beskriver i bokningen. Det betyder att du alltid betalar rätt pris för rätt kapacitet.",
        },
        {
          heading: "Expresslevrans – när varje minut räknas",
          content:
            "Ibland räcker inte samma dag – du behöver leveransen nu. Vår expresstjänst prioriterar ditt uppdrag framför allt annat. Budföraren åker direkt till hämtadressen utan omvägar och kör raka vägen till leveransadressen. Expressuppdrag har högsta prioritet i vårt system och vi bekräftar upphämtning inom minuter. Expressleverans fungerar med alla fordonstyper – från elmoped till stor skåpbil. Det enda som skiljer är att ditt uppdrag köas först. För tidskritiska dokument i innerstaden kan leveranstiden vara så kort som 30 minuter. För större leveranser med skåpbil räkna med en till två timmar beroende på avstånd och trafikläge.",
        },
        {
          heading: "Budfirma för företag – avtalspriser och fasta flöden",
          content:
            "Driver du företag med återkommande leveransbehov? Vi erbjuder företagsavtal med rabatterade priser, fasta hämtningstider och dedikerade budförare som lär känna ditt företag. Advokatbyråer som skickar dokument dagligen, restauranger som tar emot varor från leverantörer, e-handlare som behöver returer hanterade och arkitektkontor som skickar materialprover till kunder – alla får ett skräddarsytt upplägg. Med ett företagsavtal slipper du boka varje gång. Vi hämtar och levererar enligt schema, och du får en samlad faktura i slutet av månaden. Kontakta oss för att diskutera ett upplägg som passar ditt företag.",
        },
        {
          heading: "Varför skicka bud med Ungdomskraft?",
          content:
            "Ungdomskraft startade 2014 med en enkel idé: unga människor med energi och vilja ska få möjlighet att jobba, och kunder ska få hjälp som är snabbare och billigare än alternativen. Den idén genomsyrar fortfarande allt vi gör. Våra budförare är unga, snabba och hungriga – bokstavligen och bildligt. De tar trappor två steg i taget, hittar smarta vägar genom trafiken och behandlar ditt gods som om det vore deras eget. Genom att boka med oss stödjer du ungdomars väg in på arbetsmarknaden samtidigt som du får en budtjänst som överträffar de stora aktörernas leveranstider. Vi är inte ett anonymt budföretag med ett callcenter – vi är ett team av ungdomar som brinner för att leverera snabbt och göra ett bra jobb. Varje gång du bokar ett bud med Ungdomskraft ger du en ung människa chansen att visa vad den kan.",
        },
      ],
    },

    faq: [
      {
        question: "Hur snabbt kan ni leverera?",
        answer:
          "Med expressleverans kan vi ofta leverera inom 30–60 minuter i innerstaden. Standardleverans samma dag innebär leverans inom 2–4 timmar. Exakt tid beror på avstånd, trafikläge och fordonstyp.",
      },
      {
        question: "Vad kostar det att skicka bud?",
        answer:
          "Priset beror på fordon, avstånd och tid. Extra Liten (elmoped) från 595 kr, Small (liten bil) från 995 kr, Medium (skåpbil) från 1 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan ni köra en EU-pall?",
        answer:
          "Ja, vi har stor skåpbil med bakgavellift som hanterar EU-pallar upp till 800 kg. Vi lastar och lossar med liften – ingen truck behövs på mottagande sida.",
      },
      {
        question: "Vilka fordon har ni?",
        answer:
          "Vi har el-driven moped för brev och småpaket, liten bil för medelstora leveranser, skåpbil för större gods och stor skåpbil med bakgavellift för pallar och tungt gods.",
      },
      {
        question: "Kan jag boka expressleverans?",
        answer:
          "Ja, vid bokning kan du välja expressleverans. Ditt uppdrag prioriteras då framför allt annat och budföraren åker direkt till hämtadressen utan omvägar.",
      },
      {
        question: "Var kör ni bud?",
        answer:
          "Vi kör bud i Stockholm och Göteborg med omnejd. Vi tar även uppdrag mellan städer vid behov. Kontakta oss för leveranser utanför våra huvudområden.",
      },
      {
        question: "Kan ni hämta och leverera samma dag?",
        answer:
          "Ja, de flesta buduppdrag hämtas och levereras samma dag. Vi bekräftar upphämtning och leveranstid direkt vid bokning.",
      },
      {
        question: "Hanterar ni ömtåliga föremål?",
        answer:
          "Ja, vi kör neonskyltar, konst, elektronik, tårtor och andra ömtåliga föremål. Vi rekommenderar att du förpackar ömtåliga saker ordentligt, men våra förare hanterar allt varsamt.",
      },
      {
        question: "Kan ni göra flera stopp på samma tur?",
        answer:
          "Ja, du kan lägga till flera hämtnings- och leveransadresser i samma bokning. Priset justeras efter antal stopp och total körsträcka.",
      },
      {
        question: "Erbjuder ni företagsavtal?",
        answer:
          "Ja, vi erbjuder avtalspriser för företag med återkommande leveransbehov. Du får rabatterade priser, fasta hämtningstider och samlad fakturering. Kontakta oss för offert.",
      },
      {
        question: "Kör ni på kvällar och helger?",
        answer:
          "Ja, vi kör bud även på kvällar och helger. Tillgängligheten kan variera, men du ser tillgängliga tider direkt vid bokning.",
      },
      {
        question: "Hur bokar jag ett bud?",
        answer:
          "Du bokar direkt på vår hemsida. Beskriv vad du vill skicka, ange hämtadress och leveransadress, välj tid och se priset direkt. Du kan även ringa eller maila oss.",
      },
      {
        question: "Är mina saker försäkrade under transporten?",
        answer:
          "Ja, alla leveranser är försäkrade under transporten. Vid ömtåliga eller värdefulla föremål rekommenderar vi att du meddelar oss i förväg så att vi kan vidta extra försiktighetsåtgärder.",
      },
      {
        question: "Kan ni leverera mat och dryck?",
        answer:
          "Ja, vi kör catering till events, dryckesleveranser, tårtor och andra livsmedel. Vi ser till att leveransen hanteras varsamt och levereras i tid.",
      },
      {
        question: "Vad är skillnaden mellan standardbud och expressbud?",
        answer:
          "Standardbud levereras inom 2–4 timmar. Expressbud prioriteras direkt och levereras så snabbt som möjligt – ofta inom 30–60 minuter i innerstaden. Expressbud kostar lite mer men sparar tid när varje minut räknas.",
      },
      {
        question: "Hur tungt får godset vara?",
        answer:
          "Extra Liten (elmoped) upp till 50 kg, Small (liten bil) upp till 100 kg, Medium (skåpbil) upp till 150 kg, Large (skåpbil med bakgavellift) upp till 200 kg och X-Large 300 kg+. Kontakta oss för tyngre gods.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Skicka bud i 3 enkla steg",
      step2Text:
        "Vår budförare hämtar ditt gods på angiven adress och kör direkt till mottagaren. Du får en bekräftelse när leveransen är genomförd.",
      step3Title: "Levererat",
      step3Desc:
        "Ditt gods har anlänt. Mottagaren bekräftar leveransen och du kan andas ut – uppdraget är klart.",
      ctaHeading: "Skicka bud idag",
      ctaText:
        "Beskriv vad du vill skicka, ange hämtadress och leveransadress. Du ser priset direkt och kan välja expressleverans om det är bråttom.",
    },

    en: {
      slug: "courier-service",
      meta: {
        title:
          "Book a Courier – Same Day Delivery | Ungdomskraft",
        description:
          "Book a courier with same-day delivery. From envelopes to EU pallets – we deliver by electric moped, car or van with tail lift. Express within 1h. Courier service since 2014.",
        h1: "Book a Courier",
      },

      hero: {
        heading: "Fast delivery, book a courier",
        intro:
          "We deliver everything from envelopes to pallets with a custom vehicle for maximum efficiency. The delivery. We use an electric moped for small deliveries, van with tail lift for the big & heavy stuff. With Express courier, your package is delivered within 1h.",
        heroImageAlt:
          "Ungdomskraft courier service – young workers loading packages into delivery van",
      },

      featureCards: {
        headingText: "We handle your delivery",
        sinceText: "Courier service since 2014",
        storyText:
          "Since 2014, Ungdomskraft has been running courier deliveries for individuals, businesses and event organizers. Our team of fast, young couriers delivers everything from documents to pallets – with the energy and flexibility that only young people can offer.",
        bookYourText: "Book your courier",
        bookDescText:
          "Describe what you want to send, enter pickup and delivery addresses. You see the price instantly and can choose express delivery.",
        vehicleTitleText: "The right vehicle for every delivery",
        vehicleDescText:
          "We match the vehicle to your cargo. Electric moped for letters and small packages in the city, small car for medium deliveries, van for larger goods, and large van with tail lift for pallets and heavy cargo. You only pay for the capacity you need.",
      },

      itemsSection: {
        heading: "We deliver anything – no limits",
        items: [
          {
            name: "Packages & boxes",
            description:
              "E-commerce packages, returns, sample collections and gift boxes in all sizes",
            image: "/box.webp",
          },
          {
            name: "Electronics & tech",
            description:
              "Laptops, servers, monitors and other electronics that need to arrive quickly and safely",
            image: "/electronics.webp",
          },
          {
            name: "Art & signage",
            description:
              "Neon signs, paintings, light boxes, banners and other fragile items requiring careful handling",
            image: "/painting.webp",
          },
          {
            name: "Furniture & decor",
            description:
              "Office furniture, shelves, lamps and interior items delivered to offices or homes",
            image: "/furniture.webp",
          },
          {
            name: "Appliances & machinery",
            description:
              "Spare parts, machine components, appliances and heavier goods – lifted with tail lift when needed",
            image: "/appliances.webp",
          },
          {
            name: "Bikes & sports gear",
            description:
              "Bicycles, e-scooters, gym equipment and sports gear delivered to stores, events or customers",
            image: "/road-bike.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Book a courier – fast delivery without limits",
        mainText:
          "A courier service should be fast, flexible and reliable. With Ungdomskraft, that is exactly what you get. We deliver everything from a single envelope to a full EU pallet of goods – same day, often within just a couple of hours. Our team of young, energetic couriers matches the right vehicle to what you need to send. Need to get a contract across town before four o'clock? We send an electric moped that weaves through traffic in minutes. A neon sign needs careful delivery to a restaurant? We take the car and make sure it arrives in one piece. Three EU pallets of energy drinks headed to an event? Large van with tail lift handles it smoothly. What sets us apart from traditional courier companies is the combination of speed and manpower. Our young couriers don't just carry – they hustle. Stairs, tight passages and narrow time windows are everyday business for us. We know your delivery is often urgent, and we treat every shipment as if it were the most important one of the day. We accept jobs from individuals, small businesses, restaurants, event agencies, law firms, architecture studios and everyone else who needs something delivered quickly and safely. No delivery is too small and none is too large – we make it happen.",
        preparationHeading: "How to prepare your courier job",
        preparationText:
          "Make sure the goods are packed and ready for pickup at the agreed time. Documents should be in an envelope or folder. Fragile items like neon signs or artwork should be protected with bubble wrap or cardboard. Pallets should be wrapped and clearly labeled. Let us know if there is an entry code, stairs or special instructions at pickup or delivery. If you need express delivery, book as early in the day as possible and we can often deliver within one to two hours. The more information you give us upfront, the faster we can pick up and deliver. Always provide a contact person and phone number at both pickup and delivery so our couriers can reach the right person directly.",
        additionalSections: [
          {
            heading:
              "Custom vehicles – from electric moped to tail lift",
            content:
              "We have built a fleet of vehicles that covers the full spectrum of courier deliveries. For the smallest deliveries – letters, keys, documents and small packages – we use an electric moped. It moves quickly through the city center, parks anywhere and delivers in record time. For medium packages, sample collections and fragile items like neon signs, we send a small car. It holds more than the moped but remains nimble in city traffic. When volume increases – multiple boxes, furniture or larger goods – we bring the van. It holds up to ten cubic meters and our young team loads and carries everything. For the heaviest jobs, we have a large van with tail lift. It handles EU pallets, heavy machinery and large volumes. The tail lift lets us load and unload pallets directly from loading dock to ground level without a forklift. You don't choose the vehicle yourself – we automatically match the right one based on what you describe in the booking. That means you always pay the right price for the right capacity.",
          },
          {
            heading: "Express delivery – when every minute counts",
            content:
              "Sometimes same-day isn't fast enough – you need the delivery now. Our express service prioritizes your job above everything else. The courier heads straight to the pickup address with no detours and drives directly to the delivery address. Express jobs have the highest priority in our system and we confirm pickup within minutes. Express delivery works with all vehicle types – from electric moped to large van. The only difference is that your job jumps to the front of the queue. For time-critical documents in the city center, delivery time can be as short as 30 minutes. For larger deliveries by van, expect one to two hours depending on distance and traffic.",
          },
          {
            heading:
              "Courier service for businesses – contract pricing and regular runs",
            content:
              "Running a business with recurring delivery needs? We offer business agreements with discounted rates, scheduled pickups and dedicated couriers who get to know your company. Law firms sending documents daily, restaurants receiving goods from suppliers, e-commerce businesses needing returns handled, and architecture studios sending material samples to clients – everyone gets a tailored solution. With a business agreement, you skip the booking process. We pick up and deliver on schedule, and you receive a single invoice at the end of the month. Contact us to discuss an arrangement that fits your business.",
          },
          {
            heading: "Why book a courier with Ungdomskraft?",
            content:
              "Ungdomskraft started in 2014 with a simple idea: young people with energy and drive should get the chance to work, and customers should get help that is faster and more affordable than the alternatives. That idea still drives everything we do. Our couriers are young, fast and hungry – literally and figuratively. They take stairs two steps at a time, find smart routes through traffic and treat your goods as if they were their own. By booking with us, you support young people entering the job market while getting a courier service that outperforms the big players on delivery times. We are not an anonymous courier company with a call center – we are a team of young people who are passionate about delivering fast and doing a great job. Every time you book a courier with Ungdomskraft, you give a young person the chance to show what they can do.",
          },
        ],
      },

      faq: [
        {
          question: "How fast can you deliver?",
          answer:
            "With express delivery, we can often deliver within 30–60 minutes in the city center. Standard same-day delivery means delivery within 2–4 hours. Exact time depends on distance, traffic and vehicle type.",
        },
        {
          question: "How much does the courier service cost?",
          answer:
            "The price depends on vehicle, distance and time. Extra Liten (electric moped) from SEK 595, Small (small car) from SEK 995, Medium (van) from SEK 1,495. You see the price instantly when booking.",
        },
        {
          question: "Can you deliver a EU pallet?",
          answer:
            "Yes, we have a large van with tail lift that handles EU pallets up to 800 kg. We load and unload with the lift – no forklift needed at the receiving end.",
        },
        {
          question: "What vehicles do you have?",
          answer:
            "We have electric moped for letters and small packages, small car for medium deliveries, van for larger goods, and large van with tail lift for pallets and heavy cargo.",
        },
        {
          question: "Can I book express delivery?",
          answer:
            "Yes, when booking you can choose express delivery. Your job is then prioritized above everything else and the courier heads straight to the pickup address with no detours.",
        },
        {
          question: "Where do you operate?",
          answer:
            "We operate in Stockholm and Gothenburg with surrounding areas. We also take jobs between cities when needed. Contact us for deliveries outside our main areas.",
        },
        {
          question: "Can you pick up and deliver the same day?",
          answer:
            "Yes, most courier jobs are picked up and delivered the same day. We confirm pickup and delivery time immediately when you book.",
        },
        {
          question: "Do you handle fragile items?",
          answer:
            "Yes, we deliver neon signs, artwork, electronics, cakes and other fragile items. We recommend proper packaging, but our couriers handle everything with care.",
        },
        {
          question: "Can you make multiple stops on one route?",
          answer:
            "Yes, you can add multiple pickup and delivery addresses in the same booking. The price adjusts based on number of stops and total distance.",
        },
        {
          question: "Do you offer business contracts?",
          answer:
            "Yes, we offer contract pricing for businesses with recurring delivery needs. You get discounted rates, scheduled pickups and consolidated invoicing. Contact us for a quote.",
        },
        {
          question: "Do you operate evenings and weekends?",
          answer:
            "Yes, we offer courier service on evenings and weekends. Availability may vary, but you can see available times when booking.",
        },
        {
          question: "How do I book a courier?",
          answer:
            "You book directly on our website. Describe what you want to send, enter pickup and delivery addresses, choose a time and see the price instantly. You can also call or email us.",
        },
        {
          question: "Are my goods insured during transport?",
          answer:
            "Yes, all deliveries are insured during transport. For fragile or valuable items, we recommend letting us know in advance so we can take extra precautions.",
        },
        {
          question: "Can you deliver food and beverages?",
          answer:
            "Yes, we deliver catering for events, beverage shipments, cakes and other food items. We make sure the delivery is handled carefully and arrives on time.",
        },
        {
          question:
            "What is the difference between standard and express courier?",
          answer:
            "Standard courier delivers within 2–4 hours. Express courier is prioritized immediately and delivered as fast as possible – often within 30–60 minutes in the city center. Express costs a bit more but saves time when every minute counts.",
        },
        {
          question: "How heavy can the goods be?",
          answer:
            "Extra Liten (electric moped) up to 50 kg, Small (small car) up to 100 kg, Medium (van) up to 150 kg, Large (van with tail lift) up to 200 kg and X-Large 300 kg+. Contact us for heavier cargo.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "Courier delivery in 3 easy steps",
        step2Text:
          "Our courier picks up your goods at the specified address and drives directly to the recipient. You receive confirmation when the delivery is complete.",
        step3Title: "Delivered",
        step3Desc:
          "Your goods have arrived. The recipient confirms delivery and you can breathe easy – the job is done.",
        ctaHeading: "Book a courier today",
        ctaText:
          "Describe what you want to send, enter pickup and delivery addresses. You see the price instantly and can choose express delivery if it's urgent.",
      },
    },
  },

  {
    // Budbil — config för /budbil
    // Primärt sökord: budbil (880 vol, KD 25)
    // Vinkel: Fordon + chaufför för tyngre leveranser och små transportjobb.
    // Överlapp med Ungdomskrafts transporttjänst.
    slug: "budbil",
    serviceCategory: "budtjänst",

    meta: {
      title:
        "Budbil Stockholm – Boka Budbil med Chaufför Samma Dag | Ungdomskraft",
      description:
        "Boka budbil i Stockholm med chaufför och bärhjälp. Från paket till pallar – skåpbil med bakgavellift vid behov. Snabb leverans samma dag. Budbil sedan 2014.",
      h1: "Budbil Stockholm",
    },

    hero: {
      heading: "Boka budbil med chaufför",
      intro:
        "Behöver du en budbil med chaufför som hämtar och levererar samma dag? Vi kör allt från ett par kartonger till fullastade pallar. Du får rätt fordon, starka armar och snabb leverans – utan att själv lyfta ett finger.",
      heroImage: "/hero-bud.png",
      heroImageAlt:
        "Ungdomskraft budbil – ungdomar lastar paket i skåpbil för snabb budleverans",
    },

    featureCards: {
      headingText: "Din budbil är redo",
      sinceText: "Budbil sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft kört budbil åt privatpersoner, företag och eventarrangörer i Stockholm och Göteborg. Vårt team av unga förare levererar allt från enstaka paket till tunga pallar – med energi, muskelkraft och rätt fordon.",
      bookYourText: "Boka din budbil",
      bookDescText:
        "Beskriv vad som ska hämtas, ange adresser och se priset direkt. Vi matchar rätt budbil efter ditt gods.",
      vehicleTitleText: "Rätt budbil för varje leverans",
      vehicleDescText:
        "Vi har budbil i flera storlekar: liten bil för snabba leveranser, skåpbil för större volymer och stor skåpbil med bakgavellift för pallar och tungt gods. Du betalar bara för den kapacitet du behöver.",
    },

    itemsSection: {
      heading: "Det här kör vi med budbil",
      items: [
        {
          name: "Paket & kartonger",
          description:
            "E-handelspaket, returpaket, kontorsmaterial och kartonger i alla storlekar – snabbt och smidigt",
          image: "/box.webp",
        },
        {
          name: "Elektronik & teknik",
          description:
            "Servrar, skärmar, datorer och annan elektronik som kräver varsam transport med budbil",
          image: "/electronics.webp",
        },
        {
          name: "Möbler & inredning",
          description:
            "Kontorsmöbler, hyllor, bord och inredningsdetaljer – vi bär in och ställer på plats",
          image: "/furniture.webp",
        },
        {
          name: "Vitvaror & maskiner",
          description:
            "Kyl, frys, tvättmaskin och tyngre gods – budbil med bakgavellift vid behov",
          image: "/appliances.webp",
        },
        {
          name: "Konst & skyltar",
          description:
            "Tavlor, neonskyltar, ljuslådor och annat ömtåligt som kräver varsam hantering i budbil",
          image: "/painting.webp",
        },
        {
          name: "Pallar & partileveranser",
          description:
            "EU-pallar, dryckesleveranser och större volymer – stor budbil med bakgavellift löser det",
          image: "/road-bike.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Budbil i Stockholm – fordon, chaufför och bärhjälp i ett paket",
      mainText:
        "När du behöver en budbil vill du ha mer än bara ett fordon. Du vill ha en pålitlig chaufför som hämtar, lastar, kör och levererar – helst samma dag. Med Ungdomskraft får du precis det. Vi erbjuder budbil i Stockholm och Göteborg med unga, starka förare som tar hand om hela kedjan från dörr till dörr. Det som gör vår budbil unik är kombinationen av rätt fordon och fysisk styrka. Våra ungdomar bär uppför trappor, navigerar trånga passager och lastar tungt gods med samma energi oavsett om det är första eller femte stoppet på dagen. Du slipper hyra en skåpbil och köra själv, du slipper be kompisar om hjälp och du slipper ta ledigt från jobbet. Beskriv vad du behöver transportera så matchar vi automatiskt rätt budbil: liten bil för snabba leveranser mitt i stan, skåpbil för större volymer och möbler, eller stor skåpbil med bakgavellift när det handlar om pallar och riktigt tungt gods. Priset baseras på fordon, avstånd och tid – du ser exakt vad det kostar innan du bokar. Vi kör budbil åt privatpersoner som köpt möbler på Blocket, företag som behöver leverera varor till kund, restauranger som tar emot storsändningar och eventbyråer som ska ha utrustning på plats. Ingen leverans är för liten och ingen är för stor – vår budbil löser det.",
      preparationHeading: "Förbered din budbil-leverans",
      preparationText:
        "Se till att godset är färdigpackat och tillgängligt vid hämtning. Möbler som ska isärtagas bör vara demonterade i förväg. Kartonger bör vara tejpade och märkta. Pallar ska vara plastade och stabila. Meddela oss om det finns portkod, hiss, trappor eller andra specialinstruktioner vid hämtning eller leverans. Ange kontaktperson och telefonnummer vid båda adresserna så att vår chaufför kan nå rätt person direkt. Ju mer information du ger oss i förväg, desto snabbare och smidigare blir din budbil-leverans.",
      additionalSections: [
        {
          heading: "Budbil i olika storlekar – alltid rätt fordon",
          content:
            "Vi har byggt en fordonsflotta som täcker alla typer av leveranser. För snabba stadsärenden – enstaka paket, dokument eller småsaker – skickar vi en liten budbil som tar sig smidigt genom trafiken. För medelstora leveranser som möbler, elektronik eller flera kartonger använder vi en skåpbil med gott om lastutrymme. Våra förare lastar och bär allt, oavsett vikt och volym. När det handlar om EU-pallar, tungt maskineri eller stora volymer tar vi den stora budbilen med bakgavellift. Liften hanterar gods direkt från markplan till lastbrygga utan behov av truck eller hjälpmedel på mottagande sida. Du väljer inte fordon själv – vi matchar automatiskt rätt budbil utifrån vad du beskriver. Det innebär att du alltid betalar rätt pris för rätt kapacitet, och att ditt gods alltid transporteras i ett fordon med tillräckligt med utrymme och lastförmåga.",
        },
        {
          heading: "Budbil samma dag – när det inte kan vänta",
          content:
            "De flesta som söker efter budbil behöver leveransen idag, inte imorgon. Därför erbjuder vi budbil med leverans samma dag som standard. Du bokar, vi bekräftar och vår chaufför är på plats inom ett par timmar. Behöver du det ännu snabbare kan du välja expressleverans – då prioriteras ditt uppdrag direkt och budbilen åker raka vägen till hämtadressen utan omvägar. Expressleverans fungerar med alla fordonsstorlekar och innebär att ditt gods levereras så snabbt som möjligt. I centrala Stockholm kan det handla om 30–60 minuter för mindre leveranser, och en till två timmar för större budbil-uppdrag med skåpbil.",
        },
        {
          heading: "Budbil för företag – avtal och fasta flöden",
          content:
            "Driver du ett företag med regelbundna leveransbehov? Vi erbjuder företagsavtal med rabatterade priser på budbil, schemalagda hämtningar och dedikerade förare som lär känna ditt företag och era rutiner. E-handlare som skickar varor dagligen, restauranger med leverantörsleveranser, advokatbyråer med dokumenthantering och byggföretag med materialtransporter – alla får ett skräddarsytt upplägg. Med ett företagsavtal slipper du boka varje enskilt uppdrag. Budbilen kommer enligt schema, och du får en samlad faktura i slutet av månaden. Kontakta oss för att diskutera ett avtal som passar din verksamhet.",
        },
        {
          heading: "Varför boka budbil med Ungdomskraft?",
          content:
            "Ungdomskraft startade 2014 med idén att unga människor med energi och vilja ska få möjlighet att jobba, samtidigt som kunder får hjälp som är snabbare och billigare än de stora budföretagen. Den idén genomsyrar fortfarande allt vi gör – inklusive vår budbil. Våra förare är unga, snabba och starka. De tar trappor två steg i taget, hittar smarta vägar genom trafiken och behandlar ditt gods som om det vore deras eget. Genom att boka budbil med Ungdomskraft stödjer du ungdomars väg in på arbetsmarknaden, samtidigt som du får en leveranstjänst som slår de traditionella budfirmorna på både tid och pris. Vi är inte ett anonymt budföretag med ett callcenter – vi är ett team av unga som brinner för att köra snabbt och leverera rätt. Varje gång du bokar en budbil ger du en ung människa chansen att visa vad den kan.",
        },
      ],
    },

    faq: [
      {
        question: "Vad är en budbil?",
        answer:
          "En budbil är ett fordon med chaufför som hämtar och levererar gods åt dig. Med Ungdomskraft får du dessutom bärhjälp – våra förare lastar, bär och levererar hela vägen till dörren.",
      },
      {
        question: "Hur snabbt kan budbilen vara på plats?",
        answer:
          "Med standardleverans är budbilen på plats inom 2–4 timmar. Med expressleverans prioriteras ditt uppdrag direkt och vi kan ofta leverera inom 30–60 minuter i centrala Stockholm.",
      },
      {
        question: "Vad kostar det att boka en budbil?",
        answer:
          "Priset beror på fordon, avstånd och tid. Liten bil från 995 kr, skåpbil från 1 495 kr, stor skåpbil med bakgavellift från 2 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Vilka fordon använder ni som budbil?",
        answer:
          "Vi har liten bil för snabba leveranser, skåpbil för större volymer och stor skåpbil med bakgavellift för pallar och tungt gods. Vi matchar fordon efter ditt gods.",
      },
      {
        question: "Kan budbilen ta en EU-pall?",
        answer:
          "Ja, vår stora budbil har bakgavellift och hanterar EU-pallar upp till 800 kg. Vi lastar och lossar med liften – ingen truck behövs.",
      },
      {
        question: "Ingår bärhjälp med budbilen?",
        answer:
          "Ja, bärhjälp ingår alltid. Våra förare lastar, bär uppför trappor och levererar hela vägen in. Du behöver inte lyfta ett finger.",
      },
      {
        question: "Kör budbilen bara i Stockholm?",
        answer:
          "Vi kör budbil primärt i Stockholmsområdet och Göteborgsområdet, men tar även uppdrag mellan städer vid behov. Kontakta oss för längre sträckor.",
      },
      {
        question: "Kan jag boka budbil samma dag?",
        answer:
          "Ja, de flesta budbil-uppdrag hämtas och levereras samma dag. Vi bekräftar tid direkt vid bokning.",
      },
      {
        question: "Hanterar budbilen ömtåliga föremål?",
        answer:
          "Ja, vi transporterar neonskyltar, konst, elektronik och andra ömtåliga saker. Vi rekommenderar ordentlig förpackning, men våra förare hanterar allt varsamt.",
      },
      {
        question: "Kan budbilen göra flera stopp?",
        answer:
          "Ja, du kan lägga till flera hämtnings- och leveransadresser i samma bokning. Priset justeras efter antal stopp och total körsträcka.",
      },
      {
        question: "Erbjuder ni företagsavtal för budbil?",
        answer:
          "Ja, vi erbjuder avtalspriser för företag med återkommande behov av budbil. Du får rabatterade priser, fasta hämtningstider och samlad fakturering.",
      },
      {
        question: "Kör budbilen på kvällar och helger?",
        answer:
          "Ja, vi erbjuder budbil även på kvällar och helger. Tillgängligheten kan variera – du ser lediga tider direkt vid bokning.",
      },
      {
        question: "Hur bokar jag en budbil?",
        answer:
          "Du bokar direkt på vår hemsida. Beskriv vad du vill transportera, ange hämtadress och leveransadress, och se priset direkt. Du kan även ringa eller maila oss.",
      },
      {
        question: "Är mina saker försäkrade i budbilen?",
        answer:
          "Ja, alla transporter med budbil är försäkrade. Vid ömtåliga eller värdefulla föremål rekommenderar vi att du meddelar oss i förväg så att vi vidtar extra försiktighetsåtgärder.",
      },
      {
        question: "Vad är skillnaden mellan budbil och vanligt bud?",
        answer:
          "Ett vanligt bud kan skickas med elmoped eller liten bil för lätta leveranser. En budbil innebär ett större fordon – skåpbil eller skåpbil med bakgavellift – med chaufför och bärhjälp för tyngre gods.",
      },
      {
        question: "Hur tungt gods tar budbilen?",
        answer:
          "Liten bil tar upp till 100 kg, skåpbil upp till 150 kg och stor skåpbil med bakgavellift upp till 300 kg+. Kontakta oss för tyngre gods.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Budbil i 3 enkla steg",
      step2Text:
        "Vår chaufför hämtar ditt gods, lastar budbilen och kör direkt till mottagaren. Du får en bekräftelse när leveransen är genomförd.",
      step3Title: "Levererat",
      step3Desc:
        "Ditt gods har anlänt. Chauffören har burit in och ställt på plats – uppdraget är klart.",
      ctaHeading: "Boka budbil idag",
      ctaText:
        "Beskriv vad du vill transportera, ange hämtadress och leveransadress. Du ser priset direkt och kan välja expressleverans om det är bråttom.",
    },

    en: {
      slug: "delivery-van",
      meta: {
        title:
          "Delivery Van Stockholm – Book a Van with Driver Same Day | Ungdomskraft",
        description:
          "Book a delivery van with driver in Stockholm. From packages to EU pallets – van with tail lift available. Same-day delivery with carrying help included.",
        h1: "Delivery Van Stockholm",
      },

      hero: {
        heading: "Book a delivery van with driver",
        intro:
          "Need a delivery van with a driver who picks up and delivers the same day? We handle everything from a few boxes to fully loaded pallets. You get the right vehicle, strong arms, and fast delivery – without lifting a finger.",
        heroImageAlt:
          "Ungdomskraft delivery van – young workers loading packages into van for same-day delivery",
      },

      featureCards: {
        headingText: "Your delivery van is ready",
        sinceText: "Delivery van since 2014",
        storyText:
          "Since 2014, Ungdomskraft has been running delivery vans for individuals, businesses and event organizers in Stockholm and Gothenburg. Our team of young drivers delivers everything from single packages to heavy pallets – with energy, muscle and the right vehicle.",
        bookYourText: "Book your delivery van",
        bookDescText:
          "Describe what needs to be picked up, enter addresses and see the price instantly. We match the right van to your cargo.",
        vehicleTitleText: "The right van for every job",
        vehicleDescText:
          "We have delivery vans in multiple sizes: small car for quick deliveries, van for larger volumes, and large van with tail lift for pallets and heavy goods. You only pay for the capacity you need.",
      },

      itemsSection: {
        heading: "What we deliver by van",
        items: [
          {
            name: "Packages & boxes",
            description:
              "E-commerce packages, returns, office supplies and boxes of all sizes – fast and smooth",
            image: "/box.webp",
          },
          {
            name: "Electronics & tech",
            description:
              "Servers, monitors, computers and other electronics requiring careful van transport",
            image: "/electronics.webp",
          },
          {
            name: "Furniture & decor",
            description:
              "Office furniture, shelves, tables and interior items – we carry in and place where you want",
            image: "/furniture.webp",
          },
          {
            name: "Appliances & machinery",
            description:
              "Fridges, washing machines and heavier goods – delivery van with tail lift when needed",
            image: "/appliances.webp",
          },
          {
            name: "Art & signage",
            description:
              "Paintings, neon signs, light boxes and other fragile items requiring careful van transport",
            image: "/painting.webp",
          },
          {
            name: "Pallets & bulk deliveries",
            description:
              "EU pallets, beverage deliveries and larger volumes – large delivery van with tail lift",
            image: "/road-bike.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Delivery van in Stockholm – vehicle, driver and carrying help in one package",
        mainText:
          "When you need a delivery van, you want more than just a vehicle. You want a reliable driver who picks up, loads, drives and delivers – preferably the same day. With Ungdomskraft, that is exactly what you get. We offer delivery vans in Stockholm and Gothenburg with young, strong drivers who handle the entire chain from door to door. What makes our delivery van service unique is the combination of the right vehicle and physical strength. Our young team carries up stairs, navigates tight passages and loads heavy goods with the same energy whether it is the first or fifth stop of the day. You don't need to rent a van and drive yourself, you don't need to ask friends for help, and you don't need to take time off work. Describe what you need transported and we automatically match the right van: small car for quick inner-city deliveries, van for larger volumes and furniture, or large van with tail lift for pallets and really heavy goods. The price is based on vehicle, distance and time – you see exactly what it costs before you book. We drive delivery vans for individuals who bought furniture on Blocket, businesses delivering goods to customers, restaurants receiving bulk shipments, and event agencies needing equipment on site. No delivery is too small and none is too large – our delivery van handles it.",
        preparationHeading: "Prepare your delivery van pickup",
        preparationText:
          "Make sure goods are packed and accessible at pickup. Furniture that needs disassembly should be taken apart in advance. Boxes should be taped and labeled. Pallets should be wrapped and stable. Let us know about entry codes, elevators, stairs or special instructions at either address. Provide a contact person and phone number at both pickup and delivery so our driver can reach the right person directly. The more information you give us upfront, the faster and smoother your delivery van service will be.",
        additionalSections: [
          {
            heading:
              "Delivery vans in multiple sizes – always the right vehicle",
            content:
              "We have built a fleet that covers all types of deliveries. For quick city errands – single packages, documents or small items – we send a small car that moves nimbly through traffic. For medium deliveries like furniture, electronics or multiple boxes, we use a van with plenty of cargo space. Our drivers load and carry everything, regardless of weight and volume. For EU pallets, heavy machinery or large volumes, we bring the large delivery van with tail lift. The lift handles goods directly from ground level to loading dock without needing a forklift at the receiving end. You don't choose the vehicle yourself – we automatically match the right delivery van based on what you describe. This means you always pay the right price for the right capacity, and your goods are always transported in a vehicle with sufficient space and load capacity.",
          },
          {
            heading: "Same-day delivery van – when it can't wait",
            content:
              "Most people searching for a delivery van need the delivery today, not tomorrow. That is why we offer same-day delivery as standard. You book, we confirm, and our driver is on site within a couple of hours. Need it even faster? Choose express delivery – your job gets immediate priority and the van heads straight to the pickup address with no detours. Express delivery works with all vehicle sizes and means your goods are delivered as quickly as possible. In central Stockholm, that can mean 30–60 minutes for smaller deliveries, and one to two hours for larger van jobs.",
          },
          {
            heading:
              "Delivery van for businesses – contracts and regular runs",
            content:
              "Running a business with regular delivery needs? We offer business agreements with discounted delivery van rates, scheduled pickups and dedicated drivers who learn your company and routines. E-commerce businesses shipping daily, restaurants with supplier deliveries, law firms with document handling and construction companies with material transport – everyone gets a tailored solution. With a business agreement, you skip booking each individual job. The delivery van arrives on schedule, and you receive a single invoice at the end of the month. Contact us to discuss an agreement that fits your operation.",
          },
          {
            heading: "Why book a delivery van with Ungdomskraft?",
            content:
              "Ungdomskraft started in 2014 with the idea that young people with energy and drive should get the chance to work, while customers get help that is faster and more affordable than the big courier companies. That idea still drives everything we do – including our delivery van service. Our drivers are young, fast and strong. They take stairs two steps at a time, find smart routes through traffic and treat your goods as if they were their own. By booking a delivery van with Ungdomskraft, you support young people entering the job market while getting a delivery service that beats traditional courier companies on both time and price. We are not an anonymous company with a call center – we are a team of young people passionate about driving fast and delivering right. Every time you book a delivery van, you give a young person the chance to show what they can do.",
          },
        ],
      },

      faq: [
        {
          question: "What is a delivery van service?",
          answer:
            "A delivery van service provides a vehicle with a driver who picks up and delivers goods for you. With Ungdomskraft, you also get carrying help – our drivers load, carry and deliver all the way to your door.",
        },
        {
          question: "How fast can the delivery van arrive?",
          answer:
            "With standard delivery, the van arrives within 2–4 hours. With express delivery, your job is prioritized immediately and we can often deliver within 30–60 minutes in central Stockholm.",
        },
        {
          question: "How much does a delivery van cost?",
          answer:
            "The price depends on vehicle, distance and time. Small car from SEK 995, van from SEK 1,495, large van with tail lift from SEK 2,495. You see the price instantly when booking.",
        },
        {
          question: "What vehicles do you use?",
          answer:
            "We have small cars for quick deliveries, vans for larger volumes, and large vans with tail lift for pallets and heavy goods. We match the vehicle to your cargo.",
        },
        {
          question: "Can the delivery van handle a EU pallet?",
          answer:
            "Yes, our large van has a tail lift and handles EU pallets up to 800 kg. We load and unload with the lift – no forklift needed.",
        },
        {
          question: "Is carrying help included?",
          answer:
            "Yes, carrying help is always included. Our drivers load, carry up stairs and deliver all the way inside. You don't need to lift a finger.",
        },
        {
          question: "Do you only operate in Stockholm?",
          answer:
            "We primarily operate delivery vans in the Stockholm and Gothenburg areas, but also take jobs between cities when needed. Contact us for longer distances.",
        },
        {
          question: "Can I book a delivery van the same day?",
          answer:
            "Yes, most delivery van jobs are picked up and delivered the same day. We confirm the time immediately when you book.",
        },
        {
          question: "Do you handle fragile items?",
          answer:
            "Yes, we transport neon signs, artwork, electronics and other fragile items. We recommend proper packaging, but our drivers handle everything with care.",
        },
        {
          question: "Can the van make multiple stops?",
          answer:
            "Yes, you can add multiple pickup and delivery addresses in the same booking. The price adjusts based on number of stops and total distance.",
        },
        {
          question: "Do you offer business contracts?",
          answer:
            "Yes, we offer contract pricing for businesses with recurring delivery van needs. You get discounted rates, scheduled pickups and consolidated invoicing.",
        },
        {
          question: "Do you operate evenings and weekends?",
          answer:
            "Yes, we offer delivery van service on evenings and weekends. Availability may vary – you can see available times when booking.",
        },
        {
          question: "How do I book a delivery van?",
          answer:
            "You book directly on our website. Describe what you want to transport, enter pickup and delivery addresses, and see the price instantly. You can also call or email us.",
        },
        {
          question: "Are my goods insured during transport?",
          answer:
            "Yes, all delivery van transports are insured. For fragile or valuable items, we recommend letting us know in advance so we can take extra precautions.",
        },
        {
          question:
            "What is the difference between a delivery van and a regular courier?",
          answer:
            "A regular courier can be sent by electric moped or small car for light deliveries. A delivery van means a larger vehicle – van or van with tail lift – with a driver and carrying help for heavier goods.",
        },
        {
          question: "How heavy can the goods be?",
          answer:
            "Small car up to 100 kg, van up to 150 kg, large van with tail lift up to 300 kg+. Contact us for heavier cargo.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "Delivery van in 3 easy steps",
        step2Text:
          "Our driver picks up your goods, loads the van and drives directly to the recipient. You receive confirmation when the delivery is complete.",
        step3Title: "Delivered",
        step3Desc:
          "Your goods have arrived. The driver has carried everything in and placed it where you want – the job is done.",
        ctaHeading: "Book a delivery van today",
        ctaText:
          "Describe what you want to transport, enter pickup and delivery addresses. You see the price instantly and can choose express delivery if it's urgent.",
      },
    },
  },

  {
    // Cykelbud — config för /cykelbud
    // Primärt sökord: cykelbud
    // Vinkel: Snabbt, miljövänligt bud med cykel/elmoped i innerstaden.
    // Små försändelser: dokument, nycklar, blommor, småpaket.
    // Dubbelt värdeerbjudande: nollutsläpp + unga lokala bud.
    slug: "cykelbud",
    serviceCategory: "budtjänst",

    meta: {
      title:
        "Cykelbud – Miljövänlig Budleverans Samma Dag | Ungdomskraft",
      description:
        "Boka cykelbud med leverans samma dag. Nollutsläpp, snabbt genom stan. Dokument, nycklar, blommor och småpaket. Unga lokala bud som cyklar med energi.",
      h1: "Cykelbud",
    },

    hero: {
      heading: "Snabbt och grönt cykelbud",
      intro:
        "Behöver du skicka ett dokument, nycklar eller ett litet paket? Vårt cykelbud tar sig snabbast genom innerstaden – helt utan utsläpp. Unga, lokala bud som cyklar med energi och levererar med ett leende.",
      heroImage: "/hero-cykelbud.png",
      heroImageAlt:
        "Ungdomskraft cykelbud – ung cykelkurir levererar paket snabbt och miljövänligt",
    },

    featureCards: {
      headingText: "Ditt gröna bud",
      sinceText: "Cykelbud sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft skickat cykelbud åt privatpersoner och företag. Våra unga bud cyklar genom staden med dokument, nycklar, blommor och småpaket – snabbare än bilen, bättre för miljön och med den energi som bara unga kan erbjuda.",
      bookYourText: "Boka ditt cykelbud",
      bookDescText:
        "Beskriv vad du vill skicka, ange hämtadress och leveransadress. Du ser priset direkt – enkelt och snabbt.",
      vehicleTitleText: "Cykel och elmoped – nollutsläpp",
      vehicleDescText:
        "Våra cykelbud kör med cykel eller el-driven moped. Inget bränsle, inga utsläpp, inget buller. Bara ren energi rakt genom stan. Perfekt för leveranser i innerstaden där bilen fastnar i trafiken.",
    },

    itemsSection: {
      heading: "Det här skickar du med cykelbud",
      items: [
        {
          name: "Dokument & kontrakt",
          description:
            "Avtal, fakturor, pass och andra tidskritiska dokument som måste fram snabbt och säkert",
          image: "/copy-machine.webp",
        },
        {
          name: "Nycklar & kort",
          description:
            "Lägenhetsnycklar, kontorsnycklar, passerkort och andra småsaker som inte kan vänta",
          image: "/toolbox.webp",
        },
        {
          name: "Småpaket & e-handel",
          description:
            "Mindre e-handelspaket, returer, provkollektioner och presentförpackningar",
          image: "/box.webp",
        },
        {
          name: "Blommor & presenter",
          description:
            "Buketter, tårtor, presenter och andra ömtåliga leveranser som ska fram samma dag",
          image: "/plant.webp",
        },
        {
          name: "Medicin & prover",
          description:
            "Recept, läkemedelsleveranser, labbprover och andra känsliga försändelser som kräver snabb transport",
          image: "/mini-fridge.webp",
        },
        {
          name: "Mat & dryck",
          description:
            "Luncher, catering-prover, specialkaffe och andra leveranser från restaurang eller café",
          image: "/dishes.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Cykelbud – den snabbaste och grönaste leveransen i stan",
      mainText:
        "I en stad full av trafik, enkelriktade gator och begränsade parkeringsplatser är cykeln det snabbaste sättet att leverera. Ungdomskrafts cykelbud tar sig genom innerstaden på en bråkdel av tiden det tar en bil – utan att fastna i köer, leta parkering eller bidra till utsläpp. Våra cykelbud levererar dokument, nycklar, blommor, småpaket och andra mindre försändelser samma dag, ofta inom en till två timmar. Varje leverans görs med cykel eller el-driven moped, vilket innebär noll koldioxidutsläpp och minimalt med buller. Men det handlar inte bara om miljön. Det som verkligen gör vårt cykelbud speciellt är människorna bakom det. Alla våra bud är unga, lokala ungdomar som får sin första riktiga arbetslivserfarenhet genom Ungdomskraft. De cyklar med energi och stolthet, och behandlar varje leverans som om den vore den viktigaste på dagen. Genom att boka ett cykelbud med oss får du inte bara en snabb och grön leverans – du ger en ung människa chansen att visa vad den kan. Det är budtjänst med mening. Vi tar emot cykelbud-uppdrag från privatpersoner, kontor, advokatbyråer, vårdcentraler, restauranger, blomsterhandlare och alla andra som behöver skicka något litet snabbt och miljövänligt.",
      preparationHeading: "Förbered ditt cykelbud",
      preparationText:
        "Se till att din försändelse är färdigpackad och redo vid hämtning. Dokument bör ligga i kuvert eller mapp. Ömtåliga föremål som blommor eller tårta bör vara ordentligt förpackade. Ange portkod om det finns en och meddela eventuella specialinstruktioner. Cykelbud passar försändelser upp till cirka 10 kg och storlek som ryms i en cykelväska eller ryggsäck. För tyngre eller större gods rekommenderar vi istället vårt vanliga bud med bil eller skåpbil.",
      additionalSections: [
        {
          heading: "Varför cykelbud är snabbare i stan",
          content:
            "Bilar fastnar i trafik, letar parkering och kör omvägar genom enkelriktade gator. Cykeln tar den rakaste vägen, slipper köer helt och parkerar direkt vid dörren. I innerstaden innebär det att ett cykelbud ofta är 20–40 minuter snabbare än en bil på samma sträcka. Våra bud känner staden utan och innan – de vet vilka cykelbanor som sparar tid, vilka genvägar som fungerar och hur de tar sig in i kontorsbyggnader snabbast. Det är lokalkännedom som ingen GPS kan ersätta. För tidskritiska leveranser som kontrakt, nycklar eller medicin kan de minuterna vara avgörande.",
        },
        {
          heading: "Nollutsläpp – en budtjänst som gör skillnad",
          content:
            "Varje cykelbud-leverans som ersätter en bilresa minskar utsläppen i staden. Vi kör helt utan fossila bränslen – våra bud cyklar eller använder el-driven moped som laddas med grön el. Inget avgasrör, inget dieselbuller, inga partiklar. För företag som vill minska sitt klimatavtryck är cykelbud ett enkelt och konkret steg. Du kan visa dina kunder och medarbetare att ni väljer det grönare alternativet utan att kompromissa med hastighet eller tillförlitlighet. Vi utfärdar gärna ett intyg på att era leveranser görs med nollutsläpp om ni behöver det för er hållbarhetsredovisning.",
        },
        {
          heading: "Cykelbud för företag – snabba leveranser utan bilpark",
          content:
            "Många företag har återkommande behov av snabba leveranser i stan – dokument till kunder, prover till labb, nycklar till hyresgäster eller paket till kontoret. Istället för att skicka en anställd med bil eller anlita en traditionell budfirma med skåpbil kan du använda cykelbud. Det är snabbare, billigare och grönare. Vi erbjuder företagsavtal med rabatterade priser, schemalagda hämtningar och dedikerade cykelbud som lär känna ert kontor och era rutiner. Ni slipper bokningsadministration – vi hämtar och levererar enligt schema, och ni får en samlad faktura i slutet av månaden.",
        },
        {
          heading: "Varför boka cykelbud med Ungdomskraft?",
          content:
            "När du bokar cykelbud med Ungdomskraft stödjer du två saker samtidigt: en grönare stad och ungdomars väg in på arbetsmarknaden. Alla våra cykelbud är unga människor som får sin första jobbchans genom oss. De lär sig ansvar, kundservice och att leverera i tid – färdigheter de bär med sig resten av livet. Samtidigt får du en leveranstjänst som är snabbare och mer personlig än de stora budföretagen. Våra bud springer uppför trappor, ler när de levererar och lägger ner det lilla extra som gör skillnad. Det är inte bara ett bud – det är en investering i unga människors framtid och i en bättre stad att leva i.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kan jag skicka med cykelbud?",
        answer:
          "Dokument, nycklar, småpaket, blommor, medicin, mat, presenter och andra försändelser som ryms i en cykelväska eller ryggsäck – upp till cirka 10 kg.",
      },
      {
        question: "Hur snabbt levererar cykelbud?",
        answer:
          "Cykelbud levererar vanligtvis inom 1–2 timmar i innerstaden. Med expressleverans kan vi ofta vara på plats inom 30–45 minuter.",
      },
      {
        question: "Vad kostar cykelbud?",
        answer:
          "Cykelbud börjar från 299 kr. Priset beror på avstånd och tid. Du ser exakt pris vid bokning – inga dolda kostnader.",
      },
      {
        question: "Är cykelbud verkligen utsläppsfritt?",
        answer:
          "Ja, våra cykelbud kör med cykel eller el-driven moped som laddas med grön el. Inga fossila bränslen, inga avgaser, inga partikelutsläpp.",
      },
      {
        question: "Kan cykelbud leverera i regn och snö?",
        answer:
          "Ja, våra bud kör i alla väder. Försändelsen skyddas i vattentät väska och levereras torr och hel oavsett väderlek.",
      },
      {
        question: "Var kör ni cykelbud?",
        answer:
          "Vi kör cykelbud i innerstaden och närförorterna. För leveranser längre ut rekommenderar vi vårt vanliga bud med bil eller skåpbil.",
      },
      {
        question: "Kan jag boka cykelbud samma dag?",
        answer:
          "Ja, de flesta cykelbud-uppdrag hämtas och levereras samma dag. Boka så tidigt som möjligt för bästa tillgänglighet.",
      },
      {
        question: "Hur stort får paketet vara för cykelbud?",
        answer:
          "Försändelsen ska rymmas i en cykelväska eller ryggsäck och väga max cirka 10 kg. Tänk kuvert, småpaket, bukett eller liten kartong. För större gods använd vårt vanliga bud.",
      },
      {
        question: "Erbjuder ni cykelbud för företag?",
        answer:
          "Ja, vi erbjuder företagsavtal med rabatterade priser, fasta hämtningstider och samlad fakturering. Perfekt för kontor med dagliga leveransbehov.",
      },
      {
        question: "Kan cykelbud leverera blommor?",
        answer:
          "Ja, vi levererar buketter och blommor varsamt i speciella väskor. Populärt för födelsedagar, jubileum och kontorsleveranser.",
      },
      {
        question: "Kör ni cykelbud på kvällar och helger?",
        answer:
          "Ja, vi erbjuder cykelbud även på kvällar och helger. Tillgängligheten kan variera – du ser lediga tider vid bokning.",
      },
      {
        question: "Är mina saker försäkrade under cykelbudet?",
        answer:
          "Ja, alla leveranser med cykelbud är försäkrade under transporten. Kontakta oss i förväg vid särskilt värdefulla föremål.",
      },
      {
        question: "Vad är skillnaden mellan cykelbud och vanligt bud?",
        answer:
          "Cykelbud kör med cykel eller elmoped och passar mindre försändelser i innerstaden. Vanligt bud använder bil eller skåpbil och hanterar tyngre och större gods över längre avstånd.",
      },
      {
        question: "Kan cykelbud leverera dokument som kräver signatur?",
        answer:
          "Ja, vi kan hantera leveranser som kräver mottagarens signatur. Ange det vid bokning så ser vårt bud till att rätt person signerar vid leverans.",
      },
      {
        question: "Hur bokar jag ett cykelbud?",
        answer:
          "Du bokar direkt på vår hemsida. Beskriv vad du vill skicka, ange hämtadress och leveransadress, och se priset direkt. Du kan även ringa eller maila oss.",
      },
      {
        question: "Varför är cykelbud bättre för miljön?",
        answer:
          "Cykelbud ersätter bilresor med nollutsläppstransport. Ingen diesel, inga avgaser, inget buller. Varje leverans med cykelbud istället för bil minskar stadens koldioxidutsläpp och förbättrar luftkvaliteten.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Cykelbud i 3 enkla steg",
      step2Text:
        "Vårt cykelbud hämtar din försändelse och cyklar raka vägen till mottagaren. Du får en bekräftelse när leveransen är klar.",
      step3Title: "Levererat",
      step3Desc:
        "Din försändelse har anlänt – snabbt, grönt och med ett leende. Nollutsläpp hela vägen.",
      ctaHeading: "Boka cykelbud idag",
      ctaText:
        "Beskriv vad du vill skicka, ange hämtadress och leveransadress. Du ser priset direkt – snabbt, enkelt och miljövänligt.",
    },

    en: {
      slug: "bicycle-courier",
      meta: {
        title:
          "Bicycle Courier – Eco-Friendly Same Day Delivery | Ungdomskraft",
        description:
          "Book a bicycle courier with same-day delivery. Zero emissions, fast through the city. Documents, keys, flowers and small parcels. Young local couriers cycling with energy.",
        h1: "Bicycle Courier",
      },

      hero: {
        heading: "Fast and green bicycle courier",
        intro:
          "Need to send a document, keys or a small parcel? Our bicycle courier is the fastest way through the city center – with zero emissions. Young, local couriers cycling with energy and delivering with a smile.",
        heroImageAlt:
          "Ungdomskraft bicycle courier – young bike courier delivering packages fast and eco-friendly",
      },

      featureCards: {
        headingText: "Your green courier",
        sinceText: "Bicycle courier since 2014",
        storyText:
          "Since 2014, Ungdomskraft has been sending bicycle couriers for individuals and businesses. Our young couriers cycle through the city with documents, keys, flowers and small parcels – faster than a car, better for the environment and with the energy only young people can offer.",
        bookYourText: "Book your bicycle courier",
        bookDescText:
          "Describe what you want to send, enter pickup and delivery addresses. You see the price instantly – simple and fast.",
        vehicleTitleText: "Bicycle and electric moped – zero emissions",
        vehicleDescText:
          "Our bicycle couriers ride bikes or electric mopeds. No fuel, no emissions, no noise. Just pure energy straight through the city. Perfect for inner-city deliveries where cars get stuck in traffic.",
      },

      itemsSection: {
        heading: "What you can send by bicycle courier",
        items: [
          {
            name: "Documents & contracts",
            description:
              "Agreements, invoices, passports and other time-critical documents that need to arrive fast and safe",
            image: "/copy-machine.webp",
          },
          {
            name: "Keys & cards",
            description:
              "Apartment keys, office keys, access cards and other small items that can't wait",
            image: "/toolbox.webp",
          },
          {
            name: "Small parcels & e-commerce",
            description:
              "Smaller e-commerce packages, returns, sample collections and gift boxes",
            image: "/box.webp",
          },
          {
            name: "Flowers & gifts",
            description:
              "Bouquets, cakes, gifts and other delicate deliveries that need to arrive the same day",
            image: "/plant.webp",
          },
          {
            name: "Medicine & samples",
            description:
              "Prescriptions, medication deliveries, lab samples and other sensitive shipments requiring fast transport",
            image: "/mini-fridge.webp",
          },
          {
            name: "Food & drinks",
            description:
              "Lunches, catering samples, specialty coffee and other deliveries from restaurants or cafés",
            image: "/dishes.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Bicycle courier – the fastest and greenest delivery in the city",
        mainText:
          "In a city full of traffic, one-way streets and limited parking, the bicycle is the fastest way to deliver. Ungdomskraft's bicycle couriers move through the city center in a fraction of the time it takes a car – without getting stuck in queues, searching for parking or contributing to emissions. Our bicycle couriers deliver documents, keys, flowers, small parcels and other smaller shipments same day, often within one to two hours. Every delivery is made by bicycle or electric moped, meaning zero carbon emissions and minimal noise. But it's not just about the environment. What truly makes our bicycle courier service special is the people behind it. All our couriers are young, local people getting their first real work experience through Ungdomskraft. They cycle with energy and pride, treating every delivery as if it were the most important one of the day. By booking a bicycle courier with us, you don't just get a fast and green delivery – you give a young person the chance to show what they can do. That's courier service with purpose. We accept bicycle courier jobs from individuals, offices, law firms, health clinics, restaurants, florists and everyone else who needs to send something small quickly and sustainably.",
        preparationHeading: "Prepare your bicycle courier pickup",
        preparationText:
          "Make sure your shipment is packed and ready at pickup. Documents should be in an envelope or folder. Fragile items like flowers or cakes should be properly packaged. Provide an entry code if there is one and note any special instructions. Bicycle courier deliveries are suitable for shipments up to approximately 10 kg that fit in a courier bag or backpack. For heavier or larger goods, we recommend our regular courier with car or van instead.",
        additionalSections: [
          {
            heading: "Why bicycle couriers are faster in the city",
            content:
              "Cars get stuck in traffic, search for parking and take detours through one-way streets. The bicycle takes the most direct route, skips queues entirely and parks right at the door. In the city center, this means a bicycle courier is often 20–40 minutes faster than a car on the same route. Our couriers know the city inside and out – they know which cycle paths save time, which shortcuts work and how to get into office buildings fastest. It's local knowledge that no GPS can replace. For time-critical deliveries like contracts, keys or medicine, those minutes can be decisive.",
          },
          {
            heading: "Zero emissions – a courier service that makes a difference",
            content:
              "Every bicycle courier delivery that replaces a car trip reduces emissions in the city. We operate entirely without fossil fuels – our couriers cycle or use electric mopeds charged with green electricity. No exhaust pipe, no diesel noise, no particulate matter. For businesses looking to reduce their carbon footprint, bicycle couriers are a simple and concrete step. You can show your customers and employees that you choose the greener option without compromising on speed or reliability. We're happy to issue a certificate confirming your deliveries are made with zero emissions if you need it for your sustainability reporting.",
          },
          {
            heading: "Bicycle courier for businesses – fast deliveries without a fleet",
            content:
              "Many businesses have recurring needs for fast inner-city deliveries – documents to clients, samples to labs, keys to tenants or packages to the office. Instead of sending an employee by car or hiring a traditional courier with a van, you can use a bicycle courier. It's faster, cheaper and greener. We offer business agreements with discounted rates, scheduled pickups and dedicated bicycle couriers who get to know your office and routines. You skip the booking admin – we pick up and deliver on schedule, and you receive a single invoice at the end of the month.",
          },
          {
            heading: "Why book a bicycle courier with Ungdomskraft?",
            content:
              "When you book a bicycle courier with Ungdomskraft, you support two things at once: a greener city and young people's path into the job market. All our bicycle couriers are young people getting their first job opportunity through us. They learn responsibility, customer service and how to deliver on time – skills they carry with them for the rest of their lives. At the same time, you get a delivery service that is faster and more personal than the big courier companies. Our couriers run up stairs, smile when they deliver and put in the extra effort that makes the difference. It's not just a delivery – it's an investment in young people's futures and in a better city to live in.",
          },
        ],
      },

      faq: [
        {
          question: "What can I send by bicycle courier?",
          answer:
            "Documents, keys, small parcels, flowers, medicine, food, gifts and other shipments that fit in a courier bag or backpack – up to approximately 10 kg.",
        },
        {
          question: "How fast does a bicycle courier deliver?",
          answer:
            "Bicycle couriers typically deliver within 1–2 hours in the city center. With express delivery, we can often arrive within 30–45 minutes.",
        },
        {
          question: "How much does a bicycle courier cost?",
          answer:
            "Bicycle courier starts from SEK 299. The price depends on distance and time. You see the exact price when booking – no hidden costs.",
        },
        {
          question: "Is bicycle courier really zero-emission?",
          answer:
            "Yes, our bicycle couriers ride bikes or electric mopeds charged with green electricity. No fossil fuels, no exhaust, no particulate emissions.",
        },
        {
          question: "Can bicycle couriers deliver in rain and snow?",
          answer:
            "Yes, our couriers ride in all weather. The shipment is protected in a waterproof bag and delivered dry and intact regardless of conditions.",
        },
        {
          question: "Where do you offer bicycle courier service?",
          answer:
            "We offer bicycle courier service in the city center and nearby suburbs. For deliveries further out, we recommend our regular courier with car or van.",
        },
        {
          question: "Can I book a bicycle courier the same day?",
          answer:
            "Yes, most bicycle courier jobs are picked up and delivered the same day. Book as early as possible for best availability.",
        },
        {
          question: "How large can the parcel be for bicycle courier?",
          answer:
            "The shipment should fit in a courier bag or backpack and weigh max approximately 10 kg. Think envelopes, small parcels, bouquets or small boxes. For larger goods, use our regular courier.",
        },
        {
          question: "Do you offer bicycle courier for businesses?",
          answer:
            "Yes, we offer business agreements with discounted rates, scheduled pickups and consolidated invoicing. Perfect for offices with daily delivery needs.",
        },
        {
          question: "Can bicycle couriers deliver flowers?",
          answer:
            "Yes, we deliver bouquets and flowers carefully in special bags. Popular for birthdays, anniversaries and office deliveries.",
        },
        {
          question: "Do you operate evenings and weekends?",
          answer:
            "Yes, we offer bicycle courier service on evenings and weekends. Availability may vary – you can see available times when booking.",
        },
        {
          question: "Are my items insured during bicycle courier delivery?",
          answer:
            "Yes, all bicycle courier deliveries are insured during transport. Contact us in advance for particularly valuable items.",
        },
        {
          question: "What is the difference between bicycle courier and regular courier?",
          answer:
            "Bicycle courier uses bikes or electric mopeds for smaller shipments in the city center. Regular courier uses cars or vans for heavier and larger goods over longer distances.",
        },
        {
          question: "Can bicycle couriers handle deliveries requiring a signature?",
          answer:
            "Yes, we can handle deliveries that require the recipient's signature. Note this when booking and our courier will ensure the right person signs upon delivery.",
        },
        {
          question: "How do I book a bicycle courier?",
          answer:
            "You book directly on our website. Describe what you want to send, enter pickup and delivery addresses, and see the price instantly. You can also call or email us.",
        },
        {
          question: "Why is bicycle courier better for the environment?",
          answer:
            "Bicycle couriers replace car trips with zero-emission transport. No diesel, no exhaust, no noise. Every delivery by bicycle courier instead of car reduces the city's carbon emissions and improves air quality.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "Bicycle courier in 3 easy steps",
        step2Text:
          "Our bicycle courier picks up your shipment and cycles straight to the recipient. You receive confirmation when the delivery is complete.",
        step3Title: "Delivered",
        step3Desc:
          "Your shipment has arrived – fast, green and with a smile. Zero emissions all the way.",
        ctaHeading: "Book a bicycle courier today",
        ctaText:
          "Describe what you want to send, enter pickup and delivery addresses. You see the price instantly – fast, simple and eco-friendly.",
      },
    },
  },
];
